import { useEffect, useState } from 'react';
import { userSession } from '../utils/userSession.js';
import { useData } from '../context/DataContext.js';
import ImageUrlField from './ImageUrlField.js';
import { uploadImageAsset } from '../utils/imageUpload.js';
import DashboardModal from './DashboardModal.js';
import FormTabs, { FormTabPanel } from './FormTabs.js';

const TABS = [
  { id: 'business', label: 'Business' },
  { id: 'contact', label: 'Contact' }
];

export default function AddPromoter({ isOpen, onClose, onPromoterCreated }) {
  const { refreshPromoters } = useData();
  const [tab, setTab] = useState('business');
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    contactPhone: '',
    contactEmail: '',
    websiteLink: '',
    organiserImage: '',
    reach: 'Local',
    discipline: 'Drift'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingFile, setPendingFile] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setTab('business');
      setPendingFile(null);
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/promoters',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...formData,
            userId: userSession.getUserId()
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to register promoter');
      }

      const data = await response.json();
      const organiserId = data.organiserId || data.userTypeDetails?.linkId;
      let organiserImage = formData.organiserImage;
      if (pendingFile) {
        organiserImage = await uploadImageAsset({
          type: 'organiserImage',
          entityId: organiserId,
          file: pendingFile,
          sessionToken: userSession.getUser()?.sessionToken
        });
        const update = await fetch(
          `https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/promoters/${organiserId}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              ...formData,
              organiserImage,
              userId: userSession.getUserId()
            })
          }
        );
        if (!update.ok) {
          throw new Error('Promoter saved but the image URL could not be stored.');
        }
      }

      const currentUser = userSession.getUser();
      const updatedUser = {
        ...currentUser,
        userTypes: [...currentUser.userTypes, 4],
        userTypeDetails: [
          ...currentUser.userTypeDetails,
          {
            typeId: 4,
            linkId: data.userTypeDetails.linkId,
            approved: 0,
            assignedAt: new Date().toISOString()
          }
        ]
      };
      userSession.setUser(updatedUser);

      await refreshPromoters();

      if (onPromoterCreated) {
        onPromoterCreated();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to register promoter. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <DashboardModal
      title="Register as Promoter"
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoading={isLoading}
      error={error}
      onRevealTab={setTab}
      tabs={<FormTabs tabs={TABS} active={tab} onChange={setTab} />}
    >
      <FormTabPanel id="business" active={tab}>
        <div>
          <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
            Business Name *
          </label>
          <input
            type="text"
            id="businessName"
            name="businessName"
            required
            value={formData.businessName}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="reach" className="block text-sm font-medium text-gray-700">
            Reach *
          </label>
          <select
            id="reach"
            name="reach"
            required
            value={formData.reach}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          >
            <option value="Local">Local</option>
            <option value="National">National</option>
            <option value="Global">Global</option>
          </select>
        </div>

        <div>
          <label htmlFor="websiteLink" className="block text-sm font-medium text-gray-700">
            Website Link
          </label>
          <input
            type="url"
            id="websiteLink"
            name="websiteLink"
            value={formData.websiteLink}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <ImageUrlField
          id="organiserImage"
          name="organiserImage"
          label="Logo URL"
          type="organiserImage"
          value={formData.organiserImage}
          onChange={(organiserImage) => setFormData((prev) => ({ ...prev, organiserImage }))}
          pendingFile={pendingFile}
          onPendingFile={setPendingFile}
        />
      </FormTabPanel>

      <FormTabPanel id="contact" active={tab}>
        <div>
          <label htmlFor="contactName" className="block text-sm font-medium text-gray-700">
            Contact Name *
          </label>
          <input
            type="text"
            id="contactName"
            name="contactName"
            required
            value={formData.contactName}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
            Contact Phone *
          </label>
          <input
            type="tel"
            id="contactPhone"
            name="contactPhone"
            required
            value={formData.contactPhone}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>

        <div>
          <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
            Contact Email *
          </label>
          <input
            type="email"
            id="contactEmail"
            name="contactEmail"
            required
            value={formData.contactEmail}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-slate-500 focus:ring-slate-500"
          />
        </div>
      </FormTabPanel>
    </DashboardModal>
  );
}
